import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "focusable", "autoSelect", "showOnChange", "setValueOnChange", "showOnMouseDown", "setValueOnClick", "showOnKeyDown", "autoComplete"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useMemo, useEffect } from 'react';
import { getPopupRole } from 'ariakit-utils/dom';
import { queueBeforeEvent, isFocusEventOutside } from 'ariakit-utils/events';
import { useForceUpdate, useSafeLayoutEffect, useUpdateEffect, useBooleanEvent, useEvent, useId, useForkRef } from 'ariakit-utils/hooks';
import { normalizeString } from 'ariakit-utils/misc';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useComposite } from '../composite/composite.js';
import { usePopoverAnchor } from '../popover/popover-anchor.js';

function isFirstItemAutoSelected(items, activeValue, autoSelect) {
  if (!autoSelect) return false;
  var firstItem = items.find(function (item) {
    return !item.disabled && item.value;
  });
  return (firstItem == null ? void 0 : firstItem.value) === activeValue;
}

function hasCompletionString(value, activeValue) {
  if (!activeValue) return false;
  if (value == null) return false;
  value = normalizeString(value);
  return activeValue.length > value.length && activeValue.toLowerCase().indexOf(value.toLowerCase()) === 0;
}

function isInputEvent(event) {
  return event.type === "input";
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a combobox input.
 * @see https://ariakit.org/components/combobox
 * @example
 * ```jsx
 * const state = useComboboxState();
 * const props = useCombobox({ state });
 * <Role {...props} />
 * <ComboboxPopover state={state}>
 *   <ComboboxItem value="Item 1" />
 *   <ComboboxItem value="Item 2" />
 *   <ComboboxItem value="Item 3" />
 * </ComboboxPopover>
 * ```
 */


var useCombobox = createHook(function (_ref) {
  var _state$contentElement;

  var state = _ref.state,
      _ref$focusable = _ref.focusable,
      focusable = _ref$focusable === void 0 ? true : _ref$focusable,
      _ref$autoSelect = _ref.autoSelect,
      autoSelect = _ref$autoSelect === void 0 ? false : _ref$autoSelect,
      _ref$showOnChange = _ref.showOnChange,
      showOnChange = _ref$showOnChange === void 0 ? true : _ref$showOnChange,
      _ref$setValueOnChange = _ref.setValueOnChange,
      setValueOnChange = _ref$setValueOnChange === void 0 ? true : _ref$setValueOnChange,
      _ref$showOnMouseDown = _ref.showOnMouseDown,
      showOnMouseDown = _ref$showOnMouseDown === void 0 ? true : _ref$showOnMouseDown,
      _ref$setValueOnClick = _ref.setValueOnClick,
      setValueOnClick = _ref$setValueOnClick === void 0 ? true : _ref$setValueOnClick,
      _ref$showOnKeyDown = _ref.showOnKeyDown,
      showOnKeyDown = _ref$showOnKeyDown === void 0 ? true : _ref$showOnKeyDown,
      _ref$autoComplete = _ref.autoComplete,
      autoComplete = _ref$autoComplete === void 0 ? state.list.length ? "list" : "none" : _ref$autoComplete,
      props = _objectWithoutProperties(_ref, _excluded);

  var ref = useRef(null);

  var _useForceUpdate = useForceUpdate(),
      _useForceUpdate2 = _slicedToArray(_useForceUpdate, 2),
      valueUpdated = _useForceUpdate2[0],
      forceValueUpdate = _useForceUpdate2[1];

  var hasInsertedTextRef = useRef(false); // We can only allow auto select when the combobox focus is handled via the
  // aria-activedescendant attribute. Othwerwise, the focus would move to the
  // first item on every keypress.

  autoSelect = !!autoSelect && state.virtualFocus;
  var inline = autoComplete === "inline" || autoComplete === "both"; // The current input value may differ from state.value when
  // autoComplete is either "both" or "inline", in which case it will be
  // the active item value or a combination of the input value and the active
  // item value if it's the first item and it's been auto selected. This will
  // only affect the element's value, not the combobox state.

  var value = useMemo(function () {
    if (!inline) {
      return state.value;
    }

    var firstItemAutoSelected = isFirstItemAutoSelected(state.items, state.activeValue, autoSelect);

    if (firstItemAutoSelected) {
      // If the first item is auto selected, we should append the completion
      // string to the end of the value. This will be highlited in the effect
      // below.
      if (hasCompletionString(state.value, state.activeValue)) {
        var _state$activeValue;

        var slice = ((_state$activeValue = state.activeValue) == null ? void 0 : _state$activeValue.slice(state.value.length)) || "";
        return state.value + slice;
      }

      return state.value;
    }

    return state.activeValue || state.value;
  }, [inline, state.value, state.items, autoSelect, state.activeValue]); // Highlights the completion string

  useEffect(function () {
    if (!inline) return;
    if (!state.activeValue) return;
    var firstItemAutoSelected = isFirstItemAutoSelected(state.items, state.activeValue, autoSelect);
    if (!firstItemAutoSelected) return;
    if (!hasCompletionString(state.value, state.activeValue)) return;
    var element = ref.current;
    if (!element) return;
    element.setSelectionRange(state.value.length, state.activeValue.length);
  }, [valueUpdated, inline, state.activeValue, state.items, autoSelect, state.value]); // Resets the inserted text flag when the popover is not open so we don't
  // try to auto select an item after the popover closes.

  useSafeLayoutEffect(function () {
    if (state.open) return;
    hasInsertedTextRef.current = false;
  }, [state.open]); // Auto select the first item on type. If autoSelect is true and the last
  // change was a text insertion, we automatically focus on the first
  // suggestion. This effect runs both when the value changes and when the
  // items change so we also catch async items.

  useUpdateEffect(function () {
    if (!autoSelect) return;
    if (!state.items.length) return;
    if (!hasInsertedTextRef.current) return;
    state.move(state.first());
  }, [valueUpdated, state.value, autoSelect, state.items, state.move, state.first]); // Focus on the combobox input on type.

  useUpdateEffect(function () {
    if (autoSelect) return;
    state.setActiveId(null);
  }, [valueUpdated, autoSelect, state.setActiveId]); // If it has inline auto completion, set the state value when the combobox
  // input or the combobox list lose focus.

  useEffect(function () {
    if (!inline) return;
    var combobox = ref.current;
    if (!combobox) return;
    var elements = [combobox, state.contentElement].filter(Boolean);

    var onBlur = function onBlur(event) {
      if (elements.every(function (el) {
        return isFocusEventOutside(event, el);
      })) {
        state.setValue(value);
      }
    };

    elements.forEach(function (el) {
      return el == null ? void 0 : el.addEventListener("focusout", onBlur);
    });
    return function () {
      elements.forEach(function (el) {
        return el == null ? void 0 : el.removeEventListener("focusout", onBlur);
      });
    };
  }, [inline, state.contentElement, state.setValue, value]);
  var onChangeProp = props.onChange;
  var showOnChangeProp = useBooleanEvent(showOnChange);
  var setValueOnChangeProp = useBooleanEvent(setValueOnChange);
  var onChange = useEvent(function (event) {
    onChangeProp == null ? void 0 : onChangeProp(event);
    if (event.defaultPrevented) return;
    var nativeEvent = event.nativeEvent;

    if (isInputEvent(nativeEvent)) {
      hasInsertedTextRef.current = nativeEvent.inputType === "insertText";
    }

    if (showOnChangeProp(event)) {
      state.show();
    }

    if (setValueOnChangeProp(event)) {
      state.setValue(event.target.value);
    }

    if (inline && autoSelect) {
      // The state.setValue(event.target.value) above may not trigger a state
      // update. For example, say the first item starts with "t". The user
      // starts typing "t", then the first item is auto selected and the
      // inline completion string is appended and highlited. The user then
      // selects all the text and type "t" again. This change will produce the
      // same value as the state value, and therefore the state update will
      // not trigger a re-render. We need to force a re-render here so the
      // inline completion effect will be fired.
      forceValueUpdate();
    }

    if (!autoSelect || !hasInsertedTextRef.current) {
      // If autoSelect is not set or it's not an insertion of text, focus on
      // the combobox input after changing the value.
      state.setActiveId(null);
    }
  });
  var onCompositionEndProp = props.onCompositionEnd; // When dealing with composition text (for example, when the user is typing
  // in accents or chinese characters), we need to set hasInsertedTextRef to
  // true when the composition ends. This is because the native input event
  // that's passed to the change event above will not produce a consistent
  // inputType value across browsers, so we can't rely on that there.

  var onCompositionEnd = useEvent(function (event) {
    onCompositionEndProp == null ? void 0 : onCompositionEndProp(event);
    if (event.defaultPrevented) return;
    hasInsertedTextRef.current = true;
    if (!autoSelect) return;
    forceValueUpdate();
  });
  var onMouseDownProp = props.onMouseDown;
  var showOnMouseDownProp = useBooleanEvent(showOnMouseDown);
  var onMouseDown = useEvent(function (event) {
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
    if (event.defaultPrevented) return;
    if (event.button) return;
    if (event.ctrlKey) return;
    if (!showOnMouseDownProp(event)) return;
    queueBeforeEvent(event.currentTarget, "mouseup", state.show);
  });
  var onClickProp = props.onClick;
  var setValueOnClickProp = useBooleanEvent(setValueOnClick); // When clicking on the combobox input, we should make sure the current
  // input value is set on the state and focus is set on the input only.

  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    state.setActiveId(null);

    if (setValueOnClickProp(event)) {
      state.setValue(value);
    }
  });
  var onKeyDownProp = props.onKeyDown;
  var showOnKeyDownProp = useBooleanEvent(showOnKeyDown);
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    hasInsertedTextRef.current = false;
    if (event.defaultPrevented) return;
    if (event.ctrlKey) return;
    if (event.altKey) return;
    if (event.shiftKey) return;
    if (event.metaKey) return;
    if (state.open) return;
    if (state.activeId !== null) return; // Up and Down arrow keys should open the combobox popover.

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      if (showOnKeyDownProp(event)) {
        event.preventDefault();
        state.show();
      }
    }
  });
  var onBlurProp = props.onBlur;
  var onBlur = useEvent(function (event) {
    onBlurProp == null ? void 0 : onBlurProp(event);
    if (event.defaultPrevented) return;
    hasInsertedTextRef.current = false;
  }); // This is necessary so other components like ComboboxCancel can reference
  // the combobox input in their aria-controls attribute.

  var id = useId(props.id);
  props = _objectSpread(_objectSpread({
    id: id,
    role: "combobox",
    "aria-autocomplete": autoComplete,
    "aria-haspopup": getPopupRole(state.contentElement, "listbox"),
    "aria-expanded": state.open,
    "aria-controls": (_state$contentElement = state.contentElement) == null ? void 0 : _state$contentElement.id,
    value: value
  }, props), {}, {
    ref: useForkRef(ref, props.ref),
    onChange: onChange,
    onCompositionEnd: onCompositionEnd,
    onMouseDown: onMouseDown,
    onClick: onClick,
    onKeyDown: onKeyDown,
    onBlur: onBlur
  });
  props = useComposite(_objectSpread({
    state: state,
    focusable: focusable
  }, props));
  props = usePopoverAnchor(_objectSpread({
    state: state
  }, props));
  return _objectSpread({
    autoComplete: "off"
  }, props);
});
/**
 * A component that renders a combobox input.
 * @see https://ariakit.org/components/combobox
 * @example
 * ```jsx
 * const combobox = useComboboxState();
 * <Combobox state={combobox} />
 * <ComboboxPopover state={combobox}>
 *   <ComboboxItem value="Item 1" />
 *   <ComboboxItem value="Item 2" />
 *   <ComboboxItem value="Item 3" />
 * </ComboboxPopover>
 * ```
 */

var Combobox = createComponent(function (props) {
  var htmlProps = useCombobox(props);
  return createElement("input", htmlProps);
});
export { Combobox, useCombobox };